import { Logger } from "../../utils/Logger";
import { QuillEditor, stripBase64Images } from "../../components/QuillEditor";

const CTRL_TAG = "AdminServiceItemController";

function noop(action: string): void {
    Logger.debug(`${CTRL_TAG}.${action} (no-op)`);
}

function slugify(input: string): string {
    return input
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .replace(/-{2,}/g, "-");
}

function normalizeTextareaLines(el: HTMLTextAreaElement | null): void {
    if (!el) return;
    const lines = el.value
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    el.value = lines.join("\n");
}

function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): (...args: Parameters<T>) => void {
    let t: ReturnType<typeof setTimeout> | undefined;
    return (...args: Parameters<T>) => {
        window.clearTimeout(t);
        t = window.setTimeout(() => fn(...args), ms);
    };
}

function replaceTextareaSelection(
    ta: HTMLTextAreaElement,
    nextText: string,
    selectionStartOffset = nextText.length,
    selectionEndOffset = selectionStartOffset,
): void {
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? start;
    ta.setRangeText(nextText, start, end, "end");
    ta.focus();
    ta.setSelectionRange(start + selectionStartOffset, start + selectionEndOffset);
}

function getSelectedText(ta: HTMLTextAreaElement): string {
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? start;
    return ta.value.slice(start, end);
}

function toBulletLines(source: string): string[] {
    return source
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => line.replace(/^[-*]\s+/, ""))
        .map((line) => `- ${line}`);
}

function initServiceItemDeepDsl(form: HTMLFormElement): void {
    const root = form.querySelector<HTMLElement>("[data-admin-si-deep]");
    const previewUrl = form.dataset.serviceItemDeepPreview?.trim() ?? "";
    if (!root) return;

    const ta = root.querySelector<HTMLTextAreaElement>(".admin-si-deep__ta");
    const codeOutput = root.querySelector<HTMLElement>("[data-admin-si-deep-code]");
    const htmlOutput = root.querySelector<HTMLElement>("[data-admin-si-deep-html]");
    const csrf = (form.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ?? "";

    if (!ta || !codeOutput || !htmlOutput) return;

    const syncCodeOutput = (): void => {
        codeOutput.textContent = ta.value.trim() === "" ? "입력한 DSL 코드가 여기에 그대로 표시됩니다." : ta.value;
    };

    const setHtmlState = (html: string): void => {
        const trimmed = html.trim();
        htmlOutput.textContent = trimmed === "" ? "입력 내용을 서버에서 파싱한 HTML이 여기에 표시됩니다." : trimmed;
    };

    root.querySelectorAll<HTMLButtonElement>("[data-si-deep-tab]").forEach((tab) => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.siDeepTab ?? "code";
            root.querySelectorAll<HTMLButtonElement>("[data-si-deep-tab]").forEach((btn) => {
                const active = btn === tab;
                btn.classList.toggle("is-active", active);
                btn.setAttribute("aria-selected", active ? "true" : "false");
            });
            root.querySelectorAll<HTMLElement>("[data-si-deep-panel]").forEach((panel) => {
                const active = panel.dataset.siDeepPanel === target;
                panel.classList.toggle("is-active", active);
                panel.hidden = !active;
            });
        });
    });

    const postHtmlSource = (): void => {
        if (!previewUrl || !csrf) {
            setHtmlState("");
            return;
        }
        const body = new FormData();
        body.append("deep_content", ta.value);
        body.append("_token", csrf);
        void fetch(previewUrl, { method: "POST", body, credentials: "same-origin" })
            .then((r) => r.json() as Promise<{ html?: string }>)
            .then((json) => setHtmlState(typeof json.html === "string" ? json.html : ""))
            .catch(() => setHtmlState(""));
    };

    const scheduleHtmlSource = debounce(postHtmlSource, 380);
    const refreshEditor = (): void => {
        syncCodeOutput();
        scheduleHtmlSource();
    };

    ta.addEventListener("input", refreshEditor);

    syncCodeOutput();
    if (ta.value.trim() !== "") {
        postHtmlSource();
    } else {
        setHtmlState("");
    }

    root.querySelectorAll<HTMLButtonElement>("[data-si-deep-action]").forEach((btn) => {
        btn.addEventListener("click", () => {
            const action = btn.dataset.siDeepAction ?? "";
            const selectedText = getSelectedText(ta);
            const trimmedSelection = selectedText.trim();

            switch (action) {
                case "insert-section": {
                    const template = "#SECTION section-id heading-id\n[ 섹션 제목 ]\n@lead\n설명 문장을 입력하세요.\n#END";
                    replaceTextareaSelection(ta, template, 9, 30);
                    break;
                }
                case "insert-end":
                    replaceTextareaSelection(ta, "#END");
                    break;
                case "wrap-title": {
                    const label = trimmedSelection || "섹션 제목";
                    const nextText = `[ ${label} ]`;
                    const offset = trimmedSelection ? nextText.length : 2;
                    const endOffset = trimmedSelection ? nextText.length : 2 + label.length;
                    replaceTextareaSelection(ta, nextText, offset, endOffset);
                    break;
                }
                case "insert-sep":
                    replaceTextareaSelection(ta, "---");
                    break;
                case "insert-kpi": {
                    const body = trimmedSelection || "핵심 지표 1\n---\n핵심 지표 2";
                    const nextText = `@kpi\n${body}`;
                    const offset = trimmedSelection ? nextText.length : 5;
                    const endOffset = trimmedSelection ? nextText.length : 10;
                    replaceTextareaSelection(ta, nextText, offset, endOffset);
                    break;
                }
                case "insert-list": {
                    const bullets = trimmedSelection
                        ? toBulletLines(selectedText)
                        : ["- 항목 1", "- 항목 2", "- 항목 3"];
                    replaceTextareaSelection(ta, `@list compact\n${bullets.join("\n")}`, 14);
                    break;
                }
                case "insert-h3": {
                    const heading = trimmedSelection || "중제목";
                    const nextText = `@h3 ${heading}`;
                    const offset = trimmedSelection ? nextText.length : 4;
                    const endOffset = trimmedSelection ? nextText.length : 4 + heading.length;
                    replaceTextareaSelection(ta, nextText, offset, endOffset);
                    break;
                }
                case "insert-lead": {
                    const body = trimmedSelection || "리드 문장을 입력하세요.";
                    const nextText = `@lead\n${body}`;
                    const offset = trimmedSelection ? nextText.length : 6;
                    const endOffset = trimmedSelection ? nextText.length : 6 + body.length;
                    replaceTextareaSelection(ta, nextText, offset, endOffset);
                    break;
                }
                case "insert-flow": {
                    const flow = trimmedSelection || "단계 1 → 단계 2 → 단계 3";
                    const nextText = `@flow ${flow}`;
                    const offset = trimmedSelection ? nextText.length : 6;
                    const endOffset = trimmedSelection ? nextText.length : 6 + flow.length;
                    replaceTextareaSelection(ta, nextText, offset, endOffset);
                    break;
                }
                case "insert-steps":
                    replaceTextareaSelection(
                        ta,
                        "@steps\n@step 1 단계 제목\n@mini 소제목\n설명 문장을 입력하세요.\n@step 2 다음 단계\n@mini\n보조 설명을 입력하세요.\n@end steps",
                        15,
                        20,
                    );
                    break;
                case "insert-steps-cols-2":
                    replaceTextareaSelection(
                        ta,
                        "@steps cols-2\n@step 1 단계 제목\n@mini 소제목\n설명 문장을 입력하세요.\n@step 2 다음 단계\n@mini\n보조 설명을 입력하세요.\n@end steps",
                        22,
                        27,
                    );
                    break;
                case "insert-features":
                    replaceTextareaSelection(
                        ta,
                        "@features\n@feat 특징 제목\n- 항목 1\n- 항목 2\n@end features",
                        17,
                        22,
                    );
                    break;
                case "insert-capabilities":
                    replaceTextareaSelection(
                        ta,
                        "@capabilities\n@cap 역량 제목\n- 항목 1\n- 항목 2\n@end capabilities",
                        21,
                        26,
                    );
                    break;
                case "insert-compare":
                    replaceTextareaSelection(
                        ta,
                        "@compare_heads 좌측 제목 | 우측 제목\n@compare_pair 1 비교 항목\n@pain\n문제 제목\n\n문제 설명\n@solution\n해결 제목\n\n해결 설명\n@end compare_pair",
                        15,
                        24,
                    );
                    break;
                case "insert-stats":
                    replaceTextareaSelection(
                        ta,
                        "@stats\n라벨 | 값 | 설명\n라벨 | 값 | 설명",
                        7,
                        20,
                    );
                    break;
                case "insert-pills":
                    replaceTextareaSelection(
                        ta,
                        "@pills\n키워드 1\n키워드 2\n키워드 3",
                        7,
                        14,
                    );
                    break;
                case "insert-blocks":
                    replaceTextareaSelection(
                        ta,
                        "@blocks\n@block 1 블록 제목\n@flowline 흐름 요약\n@sub 소제목\n설명 문장을 입력하세요.\n@end block\n@end blocks",
                        17,
                        22,
                    );
                    break;
                default:
                    return;
            }

            refreshEditor();
        });
    });
}

function initBodyEditor(form: HTMLFormElement): void {
    const editorEl = document.getElementById("si-body-editor");
    const bodyInput = document.getElementById("si-body") as HTMLTextAreaElement | null;
    if (!editorEl || !bodyInput) return;

    const uploadImageUrl = form.dataset.uploadImageUrl ?? "";
    const csrf = (form.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ?? "";

    let initialHtml = "";
    const initEl = document.getElementById("service-item-body-initial");
    if (initEl?.textContent) {
        try {
            initialHtml = JSON.parse(initEl.textContent) as string;
        } catch {
            initialHtml = "";
        }
    }

    const uploadConfig =
        uploadImageUrl && csrf
            ? {
                  uploadUrl: uploadImageUrl,
                  csrf,
                  fileKey: "image" as const,
                  getUrl: (json: Record<string, unknown>) =>
                      (json?.data as Record<string, unknown>)?.url as string | undefined,
              }
            : undefined;

    const quill = new QuillEditor(editorEl, {
        placeholder: "본문을 입력하세요. 이미지는 툴바에서 삽입할 수 있습니다.",
        initialHtml,
        upload: uploadConfig,
    });
    quill.init();

    form.addEventListener(
        "submit",
        () => {
            bodyInput.value = stripBase64Images(quill.getHtml());
        },
        true
    );
}

function initForm(): void {
    const form = document.querySelector<HTMLFormElement>('form#service-item-form[data-admin-ajax="1"]');
    if (!form) return;

    initBodyEditor(form);
    initServiceItemDeepDsl(form);

    const titleInput = document.getElementById("si-title") as HTMLInputElement | null;
    const slugInput = document.getElementById("si-slug") as HTMLInputElement | null;
    const summaryInput = document.getElementById("si-sum") as HTMLTextAreaElement | null;
    const introHeadingInput = document.getElementById("si-intro-heading") as HTMLInputElement | null;

    let lastAutoSlug = slugInput?.value.trim() ?? "";

    titleInput?.addEventListener("input", () => {
        if (!slugInput) return;
        const currentSlug = slugInput.value.trim();
        const nextSlug = slugify(titleInput.value);
        if (nextSlug !== "" && (currentSlug === "" || currentSlug === lastAutoSlug)) {
            slugInput.value = nextSlug;
            lastAutoSlug = nextSlug;
        }

        if (introHeadingInput && introHeadingInput.value.trim() === "") {
            introHeadingInput.placeholder = `${titleInput.value.trim() || "서비스"} 소개 섹션 제목`;
        }
    });

    slugInput?.addEventListener("input", () => {
        lastAutoSlug = slugInput.value.trim();
    });

    form.addEventListener(
        "submit",
        () => {
            normalizeTextareaLines(summaryInput);
            /* 주요 제공 서비스: # 제목 # / --- / 여러 줄 본문 DSL 유지 — 줄 정규화하지 않음 */
        },
        true,
    );
}

export const AdminServiceItemController = {
    index(): void {
        noop("index");
    },
    create(): void {
        initForm();
    },
    store(): void {
        noop("store");
    },
    edit(): void {
        initForm();
    },
    update(): void {
        noop("update");
    },
    destroy(): void {
        noop("destroy");
    },
};
